/**
 * 관절 사슬 — <b>점이 지금 어디에 있는가</b>(SR-SIM-09).
 *
 * <p>여기까지의 풀이는 모두 <b>정지 자세</b>만 있으면 됐다: 크랭크 각을 주면 로커 각이 나왔다.
 * 그런데 어떤 관절은 <b>움직이는 점을 따라간다</b>. 거북이 다리는 축 위의 핀을 향하고, 공룡 발은
 * 정강이 끝에 매달려 있다 — 그 핀이 지금 어디 있는지는 <b>사슬을 거슬러 올라가야</b> 안다.
 *
 * <p>그래서 두 가지가 필요하다. ① 핀을 실은 관절부터 뿌리까지 차례로 적용해 <b>지금 위치</b>를 얻고
 * (정방향 운동학), ② 내 부모들이 한 일을 <b>되돌린다</b> — 나는 부모 안에서 도는 것이라 부모가 이미
 * 옮겨 놓은 몫까지 또 돌면 두 번 돈다. 둘이 조상을 나눠 가지면 그 부분은 저절로 상쇄된다.
 *
 * <p>좌표는 정지 자세의 모델 좌표(cm)다 — three 의 단위가 아니다. 이 파일은 three 를 모른다.
 */
import type { Vec3 } from "./linkage";
import { acos, atan2, cos, hypot, sin } from "./det-math";   // 결정론 수학 — 엔진마다 끝자리가 갈리지 않게(FIELD-CRQ-001 D-4)

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 =>
  [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = (a: Vec3) => hypot(a[0], a[1], a[2]);
const unit = (a: Vec3): Vec3 => { const l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
const rot = (u: Vec3, th: number, v: Vec3): Vec3 => {
  const c = cos(th), s = sin(th), d = dot(u, v), cx = cross(u, v);
  return [v[0] * c + cx[0] * s + u[0] * d * (1 - c),
          v[1] * c + cx[1] * s + u[1] * d * (1 - c),
          v[2] * c + cx[2] * s + u[2] * d * (1 - c)];
};

/** 사슬을 이루는 관절 — 움직임을 빼고 <b>생김새</b>만 */
export type ChainJoint = {
  id: string;
  parent: string | null;
  /** 도는 관절인가(revolute) 미끄러지는 관절인가(prismatic) */
  prismatic: boolean;
  axis: Vec3;
  pivot: Vec3;
};

/**
 * 지금의 자세 — 관절마다 얼마나 돌았는지(rad)·얼마나 밀렸는지(cm).
 * <p><b>모르면 undefined</b>. 사슬에 하나라도 모르는 관절이 있으면 위치를 셈할 수 없고,
 * 그때는 셈한 척하지 않고 null 을 낸다.
 */
export type Pose = {
  angle: (id: string) => number | undefined;
  offset: (id: string) => Vec3 | undefined;
};

/** 관절 하나가 점에 하는 일(되돌리려면 back) */
const apply = (j: ChainJoint, pose: Pose, p: Vec3, back: boolean): Vec3 | null => {
  if (j.prismatic) {
    const v = pose.offset(j.id);
    if (!v) return null;
    return back ? sub(p, v) : add(p, v);
  }
  const th = pose.angle(j.id);
  if (th === undefined) return null;
  return add(j.pivot, rot(unit(j.axis), back ? -th : th, sub(p, j.pivot)));
};

const chainUp = (joints: Map<string, ChainJoint>, from: ChainJoint | null): ChainJoint[] => {
  const out: ChainJoint[] = [];
  for (let k = from; k; k = k.parent ? joints.get(k.parent) ?? null : null) {
    out.push(k);
    if (out.length > 64) break;                                // 스펙이 고리를 이루면 멈춘다
  }
  return out;
};

/**
 * 정지 자세의 점이 <b>지금</b> 어디에 있는가 — 관절 `id` 부터 뿌리까지 차례로 적용한다.
 * @returns 사슬에 모르는 관절이 있으면 null
 */
export function forward(joints: Map<string, ChainJoint>, id: string, point: Vec3, pose: Pose): Vec3 | null {
  let q: Vec3 | null = point;
  for (const k of chainUp(joints, joints.get(id) ?? null)) {
    q = apply(k, pose, q as Vec3, false);
    if (!q) return null;
  }
  return q;
}

/**
 * 내 <b>부모들</b>이 한 일을 되돌린다 — 나는 부모 안에서 도는 것이라 부모 몫까지 또 돌면 두 번 돈다.
 * @returns 사슬에 모르는 관절이 있으면 null
 */
export function undoParents(joints: Map<string, ChainJoint>, id: string, point: Vec3, pose: Pose): Vec3 | null {
  const me = joints.get(id);
  const ups = chainUp(joints, me?.parent ? joints.get(me.parent) ?? null : null);
  let q: Vec3 | null = point;
  for (const k of ups.reverse()) {                             // 뿌리부터 되돌려 내려온다
    q = apply(k, pose, q as Vec3, true);
    if (!q) return null;
  }
  return q;
}

/**
 * <b>따라간다(track)</b> — 정지 자세에서 나눠 가졌던 점을, 그 점을 실은 관절이 옮겨 간 자리로 계속 맞춘다.
 *
 * <p>도는 관절이면 축에 수직인 평면에서 그 점을 <b>바라보는 각</b>을 낸다. 길이는 맞추지 않는다 —
 * 링크 끝이 미끄러지는 연결(구멍에 낀 핀)이라 방향만 맞으면 된다.
 *
 * @returns 각(rad). 셈할 수 없으면 null
 */
export function trackAngle(
  joints: Map<string, ChainJoint>, id: string, src: string, pin: Vec3, pose: Pose,
): number | null {
  const me = joints.get(id);
  if (!me) return null;
  const now = forward(joints, src, pin, pose);
  if (!now) return null;
  const here = undoParents(joints, id, now, pose);
  if (!here) return null;
  const u = unit(me.axis);
  const flat = (d: Vec3): Vec3 => { const k = dot(d, u); return [d[0] - u[0] * k, d[1] - u[1] * k, d[2] - u[2] * k]; };
  const d0 = flat(sub(pin, me.pivot));
  if (len(d0) < 1e-6) return null;                             // 핀이 축 위에 있으면 바라볼 방향이 없다
  const d = flat(sub(here, me.pivot));
  return atan2(dot(u, cross(d0, d)), dot(d0, d));
}

/**
 * 미끄러지는 관절이 따라가는 경우 — 각이 아니라 <b>그만큼 평행 이동</b>한다.
 * @param scale 부모 좌표계 축별 배율(연출상 행정을 줄일 때 쓴다)
 */
export function trackOffset(
  joints: Map<string, ChainJoint>, id: string, src: string, pin: Vec3, pose: Pose, scale: Vec3 = [1, 1, 1],
): Vec3 | null {
  const now = forward(joints, src, pin, pose);
  if (!now) return null;
  const here = undoParents(joints, id, now, pose);
  if (!here) return null;
  const d = sub(here, pin);
  return [d[0] * scale[0], d[1] * scale[1], d[2] * scale[2]];
}

/**
 * <b>떠다니는 핀에 끌려 도는 로커</b>(RR 다이애드).
 *
 * <p>크랭크–로커는 크랭크 피벗이 제자리에 박혀 있다. 그런데 핀 자체가 다른 관절 위를 떠다니면
 * (공룡 정강이: 커플러 끝핀이 발을 통해 정강이를 흔든다) 크랭크 각이라는 것이 없다. 대신
 * <b>원 두 개의 교점</b>을 푼다 — 내 팔 길이만큼의 원과, 떠다니는 핀에서 커플러 길이만큼의 원.
 * 교점은 둘이라 <b>정지 자세에서 핀이 있던 쪽</b>을 계속 고른다.
 *
 * @returns 각(rad). 두 원이 닿지 않거나 셈할 수 없으면 null
 */
export function rrAngle(
  joints: Map<string, ChainJoint>, id: string, src: string, pin: Vec3, attach: Vec3, pose: Pose,
): number | null {
  const me = joints.get(id);
  if (!me) return null;
  const u = unit(me.axis), O = me.pivot;
  const flat = (d: Vec3): Vec3 => { const k = dot(d, u); return [d[0] - u[0] * k, d[1] - u[1] * k, d[2] - u[2] * k]; };
  const a0 = flat(sub(attach, O)), r = len(a0);
  if (r < 1e-6) return null;                                   // 로커 끝이 축 위에 있다
  const L = len(flat(sub(attach, pin)));
  const e1 = unit(a0), e2 = cross(u, e1);
  const branch = Math.sign(dot(e2, flat(sub(pin, O)))) || 1;   // 정지 자세에서 핀이 있던 쪽 = 풀이 가지

  const now = forward(joints, src, pin, pose);
  if (!now) return null;
  const P = flat(sub(now, O));
  const x = dot(P, e1), y = dot(P, e2), d = hypot(x, y);
  if (d < 1e-9) return null;
  const c = (r * r + d * d - L * L) / (2 * r * d);
  if (c < -1 || c > 1) return null;                            // 두 원이 닿지 않는다 — 부르는 쪽이 제자리에 둔다
  const ang = atan2(y, x) - branch * acos(c);
  return atan2(sin(ang), cos(ang));
}

/** 잘라 낸 각을 이전 값에 이어 붙인다 — ±180° 를 넘을 때 한 바퀴 튀지 않게 */
export function unwrap(now: number, prev: number): number {
  let x = now;
  while (x - prev > Math.PI) x -= 2 * Math.PI;
  while (x - prev < -Math.PI) x += 2 * Math.PI;
  return x;
}
